import React, { useState, useRef, useEffect, forwardRef } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Text,
  Flex,
  Box,
  Tooltip,
} from "@chakra-ui/react";
import {
  textSecondary,
  buttonBlueDark,
  buttonGreen,
  buttonGreenText,
  buttonGreenDark,
  buttonRed,
  buttonRedText,
  buttonRedDark,
  buttonYellow,
  buttonYellowDark,
  white,
  backgroundLight,
  newBorder,
  buttonAltGrey,
  buttonAltPrimary,
  buttonAltGreen,
  buttonAltYellow,
  buttonAltSuccess,
  buttonAltRed,
  textPrimary,
  secondary,
  color2,
  color3,
  color6,
  color5,
  color4,
  primaryAlt,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import {
  FaRegCircle,
  FaCircleHalfStroke,
  FaCircleXmark,
  FaClock,
  FaCircleCheck,
  FaCaretDown,
} from "react-icons/fa6";
import { buttons, shade } from "polished";
// import WarningModalStatusApproved from "./partials/WarningModalStatusApproved";
import WarningModal from "./partials/WarningModal";

const StatusButtonTreatmentPlanEdit = forwardRef(
  (
    {
      currentStatus,
      onUpdateStatus,
      options,
      isUnclickable,
      isBig,
      isSmall,
      isRounded,
      onChangeStatus,
      isServiceButton,
      isDisabled,
      isToggle,
      toggleHandler,
      isLong,
      isTooltip,
      tooltipLabel,
      isNoLabel,
      isServicesValid,
      resetServicesStatusToUncompleted,
      size,
      p,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const btnRef = useRef();
    const { t } = useTranslation();
    const translate = (key) => t(key);

    // STATE
    const [warningModal, setWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");
    const [warningTitle, setWarningTitle] = useState("");
    const [warningButtons, setWarningButtons] = useState(false);

    // Configuration for statuses that require a warning
    const statusWarnings = {
      approved: {
        title: t("warnings.approvedStatusWarningTitle"),
        message: t("warnings.approvedStatusWarningMessage"),
        buttons: true,
      },
      // Add more statuses and their corresponding warning messages as needed
      cancelled: {
        title: t("warnings.cancelledStatusWarningTitle"),
        message: t("warnings.cancelledStatusWarningMessage"),
        buttons: true,
      },
      ongoing: {
        title: t("warnings.approvedToOngoingStatusWarningTitle"),
        message: t("warnings.approvedToOngoingStatusWarningMessage"),
        buttons: true,
      },
      completed: {
        title: t("warnings.completedAllStatusWarningTitle"),
        message: t("warnings.completedAllStatusWarningMessage"),
        buttons: false,
      },
      planning: {
        title: t("warnings.planningStatusWarningTitle"),
        message: t("warnings.planningStatusWarningMessage"),
        buttons: true,
      },
    };

    // HANDLERS

    const handleStatusChange = (status) => {
      console.log("CHANING STATUS TO", status);
      if (isServicesValid) {
        console.log("isServicesValid is true");
        if (statusWarnings[status]) {
          setPendingStatus(status);
          setWarningTitle(statusWarnings[status].title);
          setWarningMessage(statusWarnings[status].message);
          setWarningModal(true);
          setWarningButtons(statusWarnings[status].buttons);
        } else {
          console.log("isServicesValid is false, ");
          onUpdateStatus(status);
          setIsOpen(false);
        }
      } else {
        setPendingStatus(status);
        setWarningTitle(t("warnings.noServicesWarningTitle"));
        setWarningMessage(t("warnings.noServicesWarningText"));
        setWarningModal(true);
        setWarningButtons(false);
      }
    };

    // console.log("isServicesValid", isServicesValid);
    // console.log("pendingStatus", pendingStatus);

    const confirmStatusChange = () => {
      if (pendingStatus) {
        onUpdateStatus(pendingStatus);
        if (pendingStatus === "planning") {
          // console.log("resetServicesStatusToUncompleted");
          resetServicesStatusToUncompleted();
        }
      }
      setWarningModal(false);
      setIsOpen(false);
    };

    const cancelStatusChange = () => {
      setPendingStatus(null);
      setWarningModal(false);
    };

    const handleToggleClick = () => {
      // console.log("handleToggleClick");
      toggleHandler();
      setIsOpen(!isOpen);
    };

    const handleClickOutside = (event) => {
      if (btnRef.current && !btnRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // console.log("currentStatus", currentStatus);
    // console.log("options", options);
    // console.log("pendingStatus", pendingStatus);

    if (options === "treatment") {
      options = [
        {
          value: "draft",
          label: translate("status.draft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "approved",
          label: translate("status.treatmentPlanApproved"),
          icon: FaCircleCheck,
          bg: color3,
          bgDark: buttonGreenDark,
          color: white,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },

        {
          value: "planning",
          label: translate("status.treatmentPlanPlanning"),
          icon: FaClock,
          bg: color2,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "completed",
          label: translate("status.treatmentPlanCompleted"),
          icon: FaCircleCheck,
          bg: color5,
          bgDark: buttonGreenDark,
          color: textPrimary,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelled",
          label: translate("status.treatmentPlanCancelled"),
          icon: FaCircleXmark,
          bg: color6,
          bgDark: buttonRedDark,
          color: white,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "ongoing",
          label: translate("status.ongoing"),
          icon: FaCircleHalfStroke,
          bg: color4,
          bgDark: buttonYellowDark,
          color: white,
          borderColor: buttonAltYellow,
          bgHover: "#9ebfc2",
        },
      ];
    }
    if (options === "treatment" && currentStatus === "planning") {
      options = [
        {
          value: "draft",
          label: translate("status.draft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
      ];
    } else if (options === "treatmentPublic") {
      options = [
        {
          value: "draft",
          label: translate("status.draft"),
          icon: FaRegCircle,
          bg: buttonAltGrey,
          bgDark: textSecondary,
          color: textSecondary,
          borderColor: newBorder,
          bgHover: "#ddd",
        },
        {
          value: "approved",
          label: translate("status.treatmentPlanApproved"),
          icon: FaCircleCheck,
          bg: buttonAltGreen,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },

        {
          value: "consulting",
          label: translate("status.waitingForApprovalPublic"),
          icon: FaClock,
          bg: buttonAltPrimary,
          bgDark: buttonBlueDark,
          color: textPrimary,
          borderColor: newBorder,
          bgHover: shade(0.8, buttonAltPrimary),
        },
        {
          value: "planning",
          label: translate("status.waitingForApprovalPublic"),
          icon: FaClock,
          bg: buttonAltPrimary,
          bgDark: buttonYellowDark,
          color: textPrimary,
          borderColor: buttonYellow,
          bgHover: "#9ebfc2",
        },
        {
          value: "completed",
          label: translate("status.treatmentPlanCompleted"),
          icon: FaCircleCheck,
          bg: buttonAltSuccess,
          bgDark: buttonGreenDark,
          color: buttonGreenText,
          borderColor: buttonGreen,
          bgHover: "#9ebfc2",
        },
        {
          value: "cancelled",
          label: translate("status.treatmentPlanCancelled"),
          icon: FaCircleXmark,
          bg: buttonAltRed,
          bgDark: buttonRedDark,
          color: buttonRedText,
          borderColor: buttonRed,
          bgHover: "#dda3a3",
        },
        {
          value: "ongoing",
          label: translate("status.ongoing"),
          icon: FaCircleHalfStroke,
          bg: buttonAltYellow,
          bgDark: buttonYellowDark,
          color: textPrimary,
          borderColor: buttonAltYellow,
          bgHover: "#9ebfc2",
        },
      ];
    }

    const currentStatusOption = options.find(
      (option) => option.value === currentStatus
    );

    const handleClick = () => {
      if (onChangeStatus) {
        // Call onChangeStatus when provided
        onUpdateStatus(currentStatus);
      } else {
        setIsOpen(!isOpen);
      }
    };

    // console.log("currentStatusOption", currentStatusOption);

    return (
      <>
        {/* {approvedWarningModal && (
          <WarningModalStatusApproved
            open={approvedWarningModal}
            setOpen={setApprovedWarningModal}
          />
        )} */}

        {warningModal && (
          <WarningModal
            open={warningModal}
            setOpen={setWarningModal}
            warningTitle={warningTitle}
            warningMessage={warningMessage}
            warningButtons={warningButtons}
            statusBefore={currentStatus}
            statusAfter={pendingStatus}
            confirmHandler={confirmStatusChange}
            cancelHandler={cancelStatusChange}
            pendingStatus={pendingStatus}
          />
        )}

        <Tooltip
          p="0.5rem"
          bg={shade(0.6, secondary)}
          borderRadius="1rem"
          label={tooltipLabel}
          isDisabled={!isTooltip}
        >
          <Box ref={ref} p={0} borderRadius="0.75rem">
            <Menu isLazy>
              <MenuButton
                size={size ? size : "md"}
                p={p ? p : "initial"}
                h={isSmall ? "2.25rem" : "2.5rem"}
                as={Button}
                bg={currentStatusOption ? currentStatusOption.bg : white}
                color={
                  currentStatusOption
                    ? currentStatusOption.color
                    : textSecondary
                }
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{
                  bg: shade(
                    0.3,
                    currentStatusOption ? currentStatusOption.bg : white
                  ),
                  color: isBig ? white : white,
                }}
                minWidth={
                  isBig
                    ? "160px"
                    : isSmall
                    ? "140px"
                    : isServiceButton
                    ? "140px"
                    : "170px"
                }
                maxWidth={
                  isBig
                    ? "160px"
                    : isSmall
                    ? "140px"
                    : isServiceButton
                    ? "140px"
                    : isLong
                    ? "270px"
                    : "170px"
                }
                borderRadius={
                  isRounded ? "10.75rem" : isBig ? "0.75rem" : "0.75rem"
                }
                onClick={isToggle ? handleToggleClick : handleClick} // Handle click
                _active={{
                  bg: currentStatusOption ? currentStatusOption.bg : white,
                  color: currentStatusOption
                    ? currentStatusOption.color
                    : textSecondary,
                }}
                isDisabled={isDisabled}
              >
                {currentStatusOption ? (
                  <>
                    <Flex gap={0} align="center" justify="center">
                      <Flex align="center">
                        <Icon
                          as={currentStatusOption.icon}
                          mr={isSmall ? "0.25rem" : "0.5rem"}
                          h={isSmall ? "0.65rem" : "0.65rem"}
                          w={isSmall ? "0.65rem" : "0.75rem"}
                          opacity="1"
                          position="relative"
                          // top="-1px"
                        />
                        {isNoLabel ? null : (
                          <Text
                            fontSize={
                              isBig ? "1rem" : isSmall ? "0.75rem" : "0.875rem"
                            }
                            fontWeight={isBig ? "500" : isSmall ? "600" : "500"}
                            lineHeight="1"
                            textTransform="capitalize"
                          >
                            {currentStatusOption.label}
                          </Text>
                        )}
                      </Flex>

                      {isUnclickable ? null : (
                        <Icon
                          as={FaCaretDown}
                          ml="0.25rem"
                          h="0.75rem"
                          w="0.75rem"
                        />
                      )}
                    </Flex>
                  </>
                ) : (
                  "Unknown"
                )}
              </MenuButton>

              {isUnclickable ? null : (
                <MenuList p={0} m={0} borderRadius="1rem" overflow="hidden">
                  {options
                    // .filter(
                    //   (option) =>
                    //     option.value !== currentStatus &&
                    //     option.value !== "draft"
                    // )
                    .filter((option) => {
                      // Custom filter logic based on currentStatus
                      if (currentStatus === "planning") {
                        // Example: Only allow certain statuses when current status is "planning"
                        return (
                          option.value !== "draft" &&
                          option.value !== "planning" &&
                          option.value !== "completed" &&
                          option.value !== "ongoing"
                          // option.value !== "cancelled" &&
                          // option.value !== "approved" &&
                        );
                      } else if (currentStatus === "approved") {
                        // Example: Only show statuses relevant when current status is "approved"
                        return (
                          option.value !== "draft" &&
                          option.value !== "completed" &&
                          option.value !== "approved"
                        );
                      } else if (currentStatus === "ongoing") {
                        // Example: Only show statuses relevant when current status is "approved"
                        return (
                          option.value !== "draft" &&
                          option.value !== "ongoing" &&
                          option.value !== "approved"
                        );
                      } else if (currentStatus === "completed") {
                        // Example: Only show statuses relevant when current status is "approved"
                        return (
                          option.value !== "draft" &&
                          option.value !== "ongoing" &&
                          option.value !== "approved" &&
                          option.value !== "completed"
                        );
                      } else if (currentStatus === "cancelled") {
                        // Example: Only show statuses relevant when current status is "approved"
                        return (
                          option.value !== "draft" &&
                          option.value !== "ongoing" &&
                          option.value !== "completed" &&
                          option.value !== "cancelled" &&
                          option.value !== "approved"
                        );
                      } else {
                        // Default filter: exclude the current status and "draft"
                        return (
                          option.value !== currentStatus &&
                          option.value !== "draft"
                        );
                      }
                    })
                    .map((option) => (
                      <MenuItem
                        bg={option === currentStatusOption ? primaryAlt : white}
                        key={option.value}
                        onClick={() => handleStatusChange(option.value)}
                        color={
                          option === currentStatusOption ? white : textSecondary
                        }
                        py={0}
                        px={6}
                        h="3rem"
                        borderBottom={`1px solid ${newBorder}`}
                        _hover={{
                          bg:
                            option === currentStatusOption
                              ? primaryAlt
                              : backgroundLight,
                          color:
                            option === currentStatusOption
                              ? white
                              : textPrimary,
                        }}
                      >
                        <Flex
                          align="center"
                          justify="center"
                          w="1rem"
                          h="1rem"
                          bg={option.bg}
                          borderRadius="10.25rem"
                          mr="0.5rem"
                          position="relative"
                          top="-1px"
                        >
                          <Icon
                            as={option.icon}
                            color={option.color}
                            // mr="0.5rem"
                            fontSize="0.5rem"
                            style={{
                              pointerEvents: "none",
                            }}
                          />
                        </Flex>

                        <Text
                          fontSize="0.8rem"
                          fontWeight="500"
                          lineHeight="1"
                          style={{
                            pointerEvents: "none",
                          }}
                          // color={textSecondary}
                        >
                          {option.label}
                        </Text>
                      </MenuItem>
                    ))}
                </MenuList>
              )}
            </Menu>
          </Box>
        </Tooltip>
      </>
    );
  }
);

export default StatusButtonTreatmentPlanEdit;
